/**
 * A minimal ZIP writer, so "save all my pictures to my computer" is one file
 * instead of forty downloads — and so it costs no dependency.
 *
 * Entries are stored, not deflated. PNG and JPEG are already compressed, so
 * deflate would buy a percent or two in exchange for shipping an entire
 * compressor to a child's tablet.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

class Writer {
  private parts: Uint8Array[] = [];
  length = 0;

  push(bytes: Uint8Array): void {
    this.parts.push(bytes);
    this.length += bytes.length;
  }

  u16(n: number): void {
    this.push(new Uint8Array([n & 0xff, (n >>> 8) & 0xff]));
  }

  u32(n: number): void {
    this.push(new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]));
  }

  join(): Uint8Array {
    const out = new Uint8Array(this.length);
    let at = 0;
    for (const part of this.parts) {
      out.set(part, at);
      at += part.length;
    }
    return out;
  }
}

/** MS-DOS packed date and time, which is what the format still speaks. */
function dosStamp(date: Date): { time: number; date: number } {
  const time = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const day = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time, date: day };
}

export function makeZip(entries: ZipEntry[], now: Date): Uint8Array {
  const encoder = new TextEncoder();
  const stamp = dosStamp(now);
  const body = new Writer();
  const central = new Writer();

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.bytes);
    const offset = body.length;

    body.u32(0x04034b50);
    body.u16(20); // version needed
    body.u16(0x0800); // UTF-8 names
    body.u16(0); // stored
    body.u16(stamp.time);
    body.u16(stamp.date);
    body.u32(crc);
    body.u32(entry.bytes.length);
    body.u32(entry.bytes.length);
    body.u16(name.length);
    body.u16(0);
    body.push(name);
    body.push(entry.bytes);

    central.u32(0x02014b50);
    central.u16(20); // version made by
    central.u16(20); // version needed
    central.u16(0x0800);
    central.u16(0);
    central.u16(stamp.time);
    central.u16(stamp.date);
    central.u32(crc);
    central.u32(entry.bytes.length);
    central.u32(entry.bytes.length);
    central.u16(name.length);
    central.u16(0); // extra
    central.u16(0); // comment
    central.u16(0); // disk
    central.u16(0); // internal attrs
    central.u32(0); // external attrs
    central.u32(offset);
    central.push(name);
  }

  const out = new Writer();
  out.push(body.join());
  const centralOffset = out.length;
  out.push(central.join());

  out.u32(0x06054b50);
  out.u16(0);
  out.u16(0);
  out.u16(entries.length);
  out.u16(entries.length);
  out.u32(central.length);
  out.u32(centralOffset);
  out.u16(0);

  return out.join();
}
